import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import { Menu, X } from "lucide-react";

const NAV_ITEMS = [
  { key: "services", anchor: "#services" },
  { key: "audit", anchor: "#audit" },
  { key: "nachtrust", anchor: "#nachtrust" },
  { key: "support", subpath: "/support" },
  { key: "journal", subpath: "/blog" },
  { key: "contact", anchor: "#contact" },
];

export default function Navbar({ hideLangSwitch = false }) {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Language-aware URL builders. NL lives at /..., EN mirrors under /en/...
  const homeRoot = lang === "en" ? "/en/" : "/";
  const navHref = (item) => {
    if (item.anchor) return `${homeRoot}${item.anchor}`;
    if (item.subpath) return lang === "en" ? `/en${item.subpath}` : item.subpath;
    return homeRoot;
  };
  const auditHref = `${homeRoot}#audit`;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#F7F5F0]/85 backdrop-blur-xl border-b border-[#E5E3DB]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
        <Link to={homeRoot} data-testid="nav-logo" className="flex items-center gap-3 group shrink-0">
          <img
            src="/brand/ghio-logo.svg"
            alt="Ghio"
            width="189"
            height="56"
            fetchPriority="high"
            decoding="async"
            data-testid="nav-logo-img"
            className="h-10 sm:h-14 w-auto max-w-none transition-opacity group-hover:opacity-80"
          />
        </Link>

        <nav className="hidden xl:flex items-center gap-10">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.key}
              href={navHref(item)}
              data-testid={`nav-link-${item.key}`}
              className="text-sm tracking-wide text-ink hover:text-navy transition-colors relative group"
            >
              {t(`nav.${item.key}`)}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-green transition-all group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          {!hideLangSwitch && (
          <div
            data-testid="language-switcher"
            className="flex items-center border border-navy/20 rounded-full overflow-hidden text-xs tracking-wider"
          >
            <button
              data-testid="lang-nl-btn"
              onClick={() => setLang("nl")}
              aria-label="Nederlands"
              aria-pressed={lang === "nl"}
              className={`px-5 py-3 min-w-[44px] min-h-[44px] transition-colors ${
                lang === "nl" ? "bg-navy text-[#F7F5F0]" : "text-navy/70 hover:text-navy"
              }`}
            >
              NL
            </button>
            <button
              data-testid="lang-en-btn"
              onClick={() => setLang("en")}
              aria-label="English"
              aria-pressed={lang === "en"}
              className={`px-5 py-3 min-w-[44px] min-h-[44px] transition-colors ${
                lang === "en" ? "bg-navy text-[#F7F5F0]" : "text-navy/70 hover:text-navy"
              }`}
            >
              EN
            </button>
          </div>
          )}

          <a
            data-testid="nav-cta-btn"
            href={auditHref}
            className="!hidden md:!inline-flex btn-editorial btn-editorial-primary ml-2"
          >
            {t("nav.cta")} →
          </a>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="xl:hidden flex items-center justify-center min-w-[44px] min-h-[44px] rounded-full border border-navy/20"
            aria-label={open ? "Sluit menu" : "Open menu"}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="xl:hidden bg-[#F7F5F0] border-b border-[#E5E3DB] px-6 py-6 flex flex-col gap-4"
        >
          {NAV_ITEMS.map((item) => (
            <a
              key={item.key}
              href={navHref(item)}
              onClick={() => setOpen(false)}
              className="font-serif text-2xl text-navy"
            >
              {t(`nav.${item.key}`)}
            </a>
          ))}
          <a
            href={auditHref}
            onClick={() => setOpen(false)}
            className="btn-editorial btn-editorial-primary mt-4 self-start"
          >
            {t("nav.cta")} →
          </a>
        </div>
      )}
    </header>
  );
}
